<template>
  <div class="block" :class="{ block__disabled: !formState.nc_regenerate }">
    <div class="header">
      <h4>Configura el recontacto en esta estrategia para el grupo “No Contacto"</h4>
      <h4>
        Activar configuración
        <a-tooltip>
          <template #title>
            No se encontraron equivalencias. Para poder activar esta opción haz
            <a @click="redirectToEquivalence">click aquí</a>.
          </template>
          <a-switch
            :disabled="resultsChildrens.length == 0"
            v-model:checked="formState.nc_regenerate"
            size="small"
          />
        </a-tooltip>
      </h4>
    </div>

    <a-alert
      message="Recuerda que esta opción no podrá activarse una vez que la estrategia haya sido generada."
      type="warning"
      show-icon
    />

    <div class="block block--light info">
      <InfoCircleOutlined class="icon" />
      <span class="info--text" action="Cerrar">
        Al configurar esta sección, el sistema hará automáticamente una rellamada a los contactos
        clasificados sobre el grupo de tipificación "No contacto". Solo se pueden configurar hasta 2
        vueltas por estrategia.
      </span>
    </div>
    <a-form-item
      name="nc_results_childrens"
      label="Configura tus grupos y resultados de No contacto"
    >
      <!-- <p style="margin-bottom: 12px">Configura tus grupos y resultados de No contacto</p> -->
      <a-button
        type="link"
        size="small"
        class="button_groups"
        :disabled="!formState.nc_regenerate"
        @click="openModalResults"
      >
        {{
          numberGroups > 0
            ? `Se han seleccionado ${numberGroups} ${
                numberGroups == 1 ? "grupo" : "grupos"
              } y ${numberResults} ${numberResults == 1 ? "resultado" : "resultados"}`
            : `Selecciona tus grupos y resultados de NC`
        }}
        <EditOutlined />
      </a-button>
    </a-form-item>
    <p style="margin-bottom: 12px">Configura la cantidad de recontacto</p>
    <!-- <a-form class="form" :model="formState" layout="vertical"> -->
    <a-form-item name="nc_rounds" label="N° de recontacto" required>
      <a-input-number
        v-model:value="formState.nc_rounds"
        :min="0"
        :max="2"
        size="small"
        :disabled="!formState.nc_regenerate"
      />
      <a-popover
        title="N° de recontacto"
        trigger="hover"
        placement="top"
        overlayClassName="popover-rect"
      >
        <template #content>
          Esta configuración creará una <br />nueva estrategia con los <br />contactos que se
          filtren de la<br />
          estrategia base. Recuerda que <br />sólo podrás realizar 2 vueltas por <br />estrategia.
        </template>

        <InfoCircleOutlined class="input-icon" />
      </a-popover>
    </a-form-item>
    <a-modal
      width="844px"
      class="modal-no-contact"
      v-model:visible="visible"
      centered
      :keyboard="false"
      :mask-closable="false"
      @cancel="handleCancel"
    >
      <template #title>
        <span>{{ `Grupos y resultados del “No contacto” - ${props.portfolioName}` }}</span>
        <a-popover trigger="hover" placement="bottom" overlayClassName="popover-rect">
          <template #title> Grupos y resultados del “No <br />contacto” </template>
          <template #content> Solo se mostrará los grupos de <br />“No contacto” </template>

          <InfoCircleOutlined class="input-icon" />
        </a-popover>
      </template>
      <span
        >Seleccione un grupo para habilitar sus resultados. A la derecha de cada fila se muestran
        las equivalencias configuradas para el predictivo en tu sección de tipificación, algunos
        resultados no tienen equivalencias.</span
      >
      <CustomScrollbar height="50vh" auto>
        <typification-tree
          class="typification-tree"
          v-model:expandedKeys="expandedKeys"
          v-model:checkedKeys="checkedKeys"
          checkable
          :tree-data="results"
          :childrens-data="resultsChildrens"
        />
        <!-- <TypificationTreeSelected :treeData="results" /> -->
      </CustomScrollbar>
      <template #footer>
        <a-button key="back" @click="handleCancel">Cancelar</a-button>
        <a-button key="submit" type="primary" @click="handleOk"> Guardar </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, inject, Ref } from "vue";
import { InfoCircleOutlined, EditOutlined } from "@ant-design/icons-vue";
import TypificationTree from "@/app/strategies/components/modals/typificationSelected/TypificationTree.vue";
import { fetchPortfolioResultsNC } from "@/app/strategies/services";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { cloneDeep } from "lodash";
import { useRouter } from "vue-router";

const visible = ref(false);
const formState = inject("formState") as Ref<{
  nc_regenerate: boolean;
  nc_rounds: number;
  nc_results: Array<any>;
  nc_results_childrens: Array<any>;
}>;
// const formState = ref<{
//   nc_regenerate: boolean;
//   nc_rounds: number;
//   nc_results: Array<any>;
//   nc_results_childrens: Array<any>;
// }>({
//   nc_regenerate: false,
//   nc_rounds: 0,
//   nc_results: [],
//   nc_results_childrens: [],
// });

const props = defineProps<{
  idPortfolio: number;
  portfolioName: string;
}>();

const router = useRouter();

const results = ref([]);
const resultsChildrens = ref<Array<any>>([]);

const expandedKeys = ref<string[]>([]);
const checkedKeys = ref<string[]>([]);

const numberGroups = computed(
  () => formState.nc_results.length - formState.nc_results_childrens.length
);
const numberResults = computed(() => formState.nc_results_childrens.length);

const fetchResults = async () => {
  const response = await fetchPortfolioResultsNC(props.idPortfolio);
  results.value = response.map((el: any, key: number) => {
    const children: Array<any> = el.results.map((el2: any) => ({
      key: el2.id,
      title: el2.name,
      subtitle: el2.atk_states.map((item: any) => item.name).join(", "),
      pattern: el.id,
    }));
    resultsChildrens.value = [...resultsChildrens.value, ...children];
    return { key: el.id, title: el.name, children: children, pos: key };
  });
};

watch(
  () => props.idPortfolio,
  async () => {
    if (props.idPortfolio)  await fetchResults();
  },
  { immediate: true }
);

const handleOk = () => {
  formState.nc_results = cloneDeep(checkedKeys.value);
  formState.nc_results_childrens = resultsChildrens.value
    .filter((item) => checkedKeys.value.includes(item.key))
    .map((el) => el.key);
  visible.value = false;
};

const handleCancel = () => {
  checkedKeys.value = cloneDeep(formState.nc_results);
  visible.value = false;
};

const getAllKeys = (nodes: any[]) => {
  let keys: string[] = [];

  const traverse = (node: any) => {
    keys.push(node.key);
    if (node.children) {
      node.children.forEach((child: any) => traverse(child));
    }
  };

  nodes.forEach((node) => traverse(node));
  return keys;
};

watch(
  () => results.value,
  (value) => {
    expandedKeys.value = getAllKeys(value);
    checkedKeys.value = [];
  },
  { immediate: true }
);

const openModalResults = async () => {
  // await fetchResults();
  visible.value = true;
};

const redirectToEquivalence = () => {
  const url = router.resolve(`/operations/campaigns/${props.idPortfolio}`).href; // Resolve the route to a URL
  window.location.href = url;
};

defineExpose({ formState });
</script>

<style lang="sass" scoped>
.block
  padding: 16px 12px 20px 12px
  margin: 12px 0
  border: 1px solid $gray-6
  border-radius: 8px

  &--light
    border: 1px solid #dcdcdc
    color: $gray-7
    padding: 8px 12px
    margin: 8px 0 12px 0

  &__disabled
    background-color: $gray-3

.info
  display: flex

  &--text
    font-size: 12px

.icon
  margin: 3px 6px 0 0

.input-icon
  margin-left: 8px

.form
  flex: 1 1 auto
  min-height: 0
  margin-left: 8px

.button_groups
  margin-bottom: 0

.header
  display: flex
  justify-content: space-between
  margin-bottom: 8px

:global(.popover-rect .ant-popover-inner)
  border-radius: 1px !important
  padding: 0 !important

:global(.popover-rect .ant-popover-title)
  padding: 4px 12px
  border-bottom: 1px solid #e8e8e8 !important

:global(.popover-rect .ant-popover-inner-content)
  padding: 0 12px 12px 12px

:global(.modal-no-contact .ant-modal-content)
  padding: 0 !important

:global(.modal-no-contact .ant-modal-title)
  padding: 20px 24px 0 24px
  border-bottom: 1px solid #e8e8e8 !important

:global(.modal-no-contact .ant-modal-body)
  padding: 10px 24px 0 24px

:global(.modal-no-contact .ant-modal-footer)
    padding: 10px 24px 15px 24px
    margin-top: 0
    border-top: 1px solid #e8e8e8 !important
</style>
